/**
 * Unified Module Permissions Hook v2
 * 
 * Uses Navigation API data (single source of truth) instead of making separate API calls.
 * All modules should use this hook for consistent RBAC checks.
 * 
 * Features:
 * - Extracts permissions from Navigation API (already fetched by useNavigation)
 * - Supports view mode toggling (personal vs admin)
 * - Provides data filters for queries
 * - Zero additional API calls (uses cached navigation data)
 * - Consistent permission structure across all modules
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigation } from './useNavigation';

export type ModuleViewMode = 'personal' | 'admin';
export type ModuleAccessLevel = 'user' | 'employee' | 'supervisor' | 'admin' | 'owner';

export interface ModulePermissions {
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_manage_users: boolean;
  can_approve: boolean;
  can_settings: boolean;
  can_admin: boolean;
}

export interface DataFilter {
  isPersonalView: boolean;
  userId?: number;
  clientId?: number;
  canAccessAll: boolean;
  accessLevel: ModuleAccessLevel;
}

export interface UseModulePermissionsReturn {
  // Core permissions
  permissions: ModulePermissions;
  
  // View mode control
  viewMode: ModuleViewMode;
  accessLevel: ModuleAccessLevel;
  canToggleView: boolean;
  toggleViewMode: () => void;
  setViewMode: (mode: ModuleViewMode) => void;
  
  // User context
  currentUserId: number | null;
  currentClientId: number | null;
  userRole: string | null;
  
  // Data filtering
  getDataFilter: () => DataFilter;
  
  // Loading state
  loading: boolean;
  error: string | null;
  
  // Module metadata
  moduleData: any;
}

/**
 * Unified hook for module permissions
 * 
 * @param moduleKey - The kebab-case module key (e.g., 'total-attendance', 'total-finance')
 * @returns Module permissions and utility functions
 * 
 * @example
 * ```tsx
 * const { 
 *   permissions, 
 *   viewMode, 
 *   toggleViewMode, 
 *   getDataFilter 
 * } = useModulePermissions('total-attendance');
 * 
 * if (permissions.can_admin) {
 *   // Show admin menu
 * }
 * ```
 */
export const useModulePermissions = (moduleKey: string): UseModulePermissionsReturn => {
  const { user, navigationData, loading: navLoading, userRole } = useNavigation();
  
  const [viewMode, setViewModeState] = useState<ModuleViewMode>('personal');

  // Extract module data from navigation
  const moduleData = useMemo(() => {
    if (!navigationData || !moduleKey) return null;

    // Search through all navigation groups to find this module
    for (const group of Object.values(navigationData)) {
      const module = (group as any).items?.find((item: any) => item.module_key === moduleKey);
      if (module) {
        return module;
      }
    }
    
    return null;
  }, [navigationData, moduleKey]);

  // Extract permissions from module data
  const permissions = useMemo<ModulePermissions>(() => {
    if (!moduleData?.user_permissions) {
      // No permissions found - return all false
      return {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
        can_manage_users: false,
        can_approve: false,
        can_settings: false,
        can_admin: false,
      };
    }

    const perms = moduleData.user_permissions;
    
    console.log(`[MODULE PERMISSIONS v2] Loaded permissions for ${moduleKey}:`, {
      userId: user?.id,
      userRole: moduleData.user_role,
      permissions: perms
    });

    return {
      can_view: perms.can_view ?? false,
      can_create: perms.can_create ?? false,
      can_edit: perms.can_edit ?? false,
      can_delete: perms.can_delete ?? false,
      can_manage_users: perms.can_manage_users ?? false,
      can_approve: perms.can_approve ?? false,
      can_settings: perms.can_settings ?? false,
      can_admin: perms.can_admin ?? false,
    };
  }, [moduleData, user, moduleKey]);

  // Determine access level from role and permissions
  const accessLevel = useMemo<ModuleAccessLevel>(() => {
    if (!moduleData) return 'user';
    
    const role = moduleData.user_role || userRole;
    
    // Owner has highest level
    if (role === 'owner') return 'owner';
    
    // Admin role or can_admin permission
    if (role === 'admin' || permissions.can_admin) return 'admin';
    
    // Supervisor: can delete or has full CRUD
    if (permissions.can_delete || (permissions.can_create && permissions.can_edit)) {
      return 'supervisor';
    }
    
    // Employee: from employees table (has create but limited edit)
    if (moduleData.access_context === 'employee') return 'employee';
    
    // Default to user
    return 'user';
  }, [moduleData, userRole, permissions]);

  // Can toggle view if user has admin/supervisor access
  const canToggleView = useMemo(() => {
    return accessLevel === 'admin' || accessLevel === 'owner' || accessLevel === 'supervisor';
  }, [accessLevel]);

  // Set default view mode based on access level
  useEffect(() => {
    if (!moduleData) return;

    // Admins and owners default to admin view
    queueMicrotask(() => {
      if (accessLevel === 'admin' || accessLevel === 'owner') {
        setViewModeState('admin');
      } else {
        setViewModeState('personal');
      }
    });
  }, [moduleData, accessLevel]);

  // Toggle between personal and admin view
  const toggleViewMode = useCallback(() => {
    if (!canToggleView) return;
    
    setViewModeState(prev => {
      const newMode = prev === 'personal' ? 'admin' : 'personal';
      console.log(`[MODULE PERMISSIONS v2] View mode changed: ${prev} → ${newMode}`);
      return newMode;
    });
  }, [canToggleView]);

  // Set view mode explicitly
  const setViewMode = useCallback((mode: ModuleViewMode) => {
    if (!canToggleView && mode === 'admin') {
      console.warn(`[MODULE PERMISSIONS v2] Cannot set admin view - insufficient permissions`);
      return;
    }
    
    console.log(`[MODULE PERMISSIONS v2] View mode set to: ${mode}`);
    setViewModeState(mode);
  }, [canToggleView]);

  // Get data filter for queries
  const getDataFilter = useCallback((): DataFilter => {
    const isPersonalView = viewMode === 'personal';
    const clientId = (user as any)?.client_id || (user as any)?.clientId;
    
    return {
      isPersonalView,
      userId: isPersonalView ? user?.id : undefined,
      clientId,
      canAccessAll: !isPersonalView && (accessLevel === 'admin' || accessLevel === 'owner'),
      accessLevel,
    };
  }, [viewMode, user, accessLevel]);

  return {
    // Core permissions
    permissions,
    
    // View mode control
    viewMode,
    accessLevel,
    canToggleView,
    toggleViewMode,
    setViewMode,
    
    // User context
    currentUserId: user?.id || null,
    currentClientId: (user as any)?.client_id || (user as any)?.clientId || null,
    userRole: moduleData?.user_role || userRole || null,
    
    // Data filtering
    getDataFilter,
    
    // Loading state
    loading: navLoading,
    error: null,
    
    // Module metadata
    moduleData,
  };
};

export default useModulePermissions;
